<template>
  <div class="campaign-picker flex items-center gap-2" data-campaign-picker>
    <!-- Custom Dropdown -->
    <div class="flex-1 relative">
      <!-- Trigger Button -->
      <button
        @click="isOpen = !isOpen"
        class="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-left flex items-center justify-between text-sm focus:border-eggplant-400 focus:outline-hidden focus:ring-3 focus:ring-eggplant-500/10 dark:focus:border-eggplant-800 placeholder:text-gray-400 dark:placeholder:text-white/30"
      >
        <span
          :class="
            selectedCampaignName
              ? 'text-gray-900 dark:text-gray-100'
              : 'text-gray-400 dark:text-gray-500'
          "
          >{{ selectedCampaignName || "Select campaign..." }}</span
        >
        <ChevronDownIcon
          :class="['w-4 h-4 text-gray-400 transition-transform', isOpen ? 'rotate-180' : '']"
        />
      </button>

      <!-- Dropdown Content -->
      <div
        v-if="isOpen"
        class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg"
      >
        <!-- Search Input -->
        <div class="p-3 border-b border-gray-200 dark:border-gray-600">
          <div class="relative">
            <MagnifyingGlassIcon
              class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
            />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search campaigns..."
              class="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
            />
            <button
              v-if="searchQuery"
              @click="clearSearch"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 p-0.5 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              <XMarkIcon class="w-3 h-3 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <!-- Campaign List -->
        <div ref="campaignListRef" class="max-h-48 overflow-y-auto scroll-smooth">
          <!-- Select All option (only for multiple mode) -->
          <div
            v-if="multiple"
            @click="!hasUnsyncedCampaigns && toggleSelectAll()"
            :class="[
              'flex items-center p-3 border-b border-gray-200 dark:border-gray-600',
              hasUnsyncedCampaigns
                ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer',
            ]"
            :title="
              hasUnsyncedCampaigns
                ? 'Cannot select all campaigns because some have not been synced from your CRM yet'
                : ''
            "
          >
            <div class="relative mr-3">
              <input
                type="checkbox"
                :checked="selectedCampaigns.includes('all')"
                :disabled="hasUnsyncedCampaigns"
                class="sr-only"
                readonly
              />
              <div
                :class="[
                  'h-4 w-4 rounded border flex items-center justify-center transition-all duration-200',
                  selectedCampaigns.includes('all')
                    ? 'bg-brand-500 border-brand-500'
                    : hasUnsyncedCampaigns
                      ? 'border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800'
                      : 'border-gray-300 bg-white hover:border-gray-400',
                ]"
              >
                <svg
                  v-if="selectedCampaigns.includes('all')"
                  class="h-3 w-3 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="3"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <div class="flex-1 flex items-center gap-2">
              <span
                :class="[
                  'text-sm font-medium',
                  hasUnsyncedCampaigns
                    ? 'text-gray-400 dark:text-gray-500'
                    : 'text-gray-900 dark:text-gray-100',
                ]"
              >
                All campaigns
              </span>
              <span
                v-if="hasUnsyncedCampaigns"
                class="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full whitespace-nowrap"
              >
                Some not synced
              </span>
            </div>
          </div>

          <!-- Individual campaigns -->
          <div
            v-for="campaign in filteredCampaigns"
            :key="campaign.id"
            :data-campaign-id="campaign.id"
            @click="!isCampaignDisabled(campaign) && selectCampaign(campaign)"
            :class="[
              'flex items-center p-3 border-b border-gray-100 dark:border-gray-600 last:border-b-0',
              isCampaignDisabled(campaign)
                ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer',
            ]"
            :title="
              isCampaignDisabled(campaign)
                ? 'This campaign must be synced from your CRM before you can use it in Deep Insights'
                : ''
            "
          >
            <!-- Checkbox for multiple selection -->
            <div v-if="multiple" class="relative mr-3">
              <input
                type="checkbox"
                :checked="
                  selectedCampaigns.includes(campaign.id) || selectedCampaigns.includes('all')
                "
                :disabled="isCampaignDisabled(campaign)"
                class="sr-only"
                readonly
              />
              <div
                :class="[
                  'h-4 w-4 rounded border flex items-center justify-center transition-all duration-200',
                  selectedCampaigns.includes(campaign.id) || selectedCampaigns.includes('all')
                    ? 'bg-brand-500 border-brand-500'
                    : isCampaignDisabled(campaign)
                      ? 'border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800'
                      : 'border-gray-300 bg-white hover:border-gray-400',
                ]"
              >
                <svg
                  v-if="
                    selectedCampaigns.includes(campaign.id) || selectedCampaigns.includes('all')
                  "
                  class="h-3 w-3 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="3"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            <div class="flex-1 flex items-center gap-2">
              <span
                :class="[
                  'text-sm',
                  isCampaignDisabled(campaign)
                    ? 'text-gray-400 dark:text-gray-500'
                    : 'text-gray-900 dark:text-gray-100',
                ]"
                >{{ campaign.name }}</span
              >
              <span
                v-if="isCampaignDisabled(campaign)"
                class="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full whitespace-nowrap"
              >
                Not synced
              </span>
            </div>

            <!-- Check icon for single selection -->
            <CheckIcon
              v-if="!multiple && selectedCampaign === campaign.id"
              class="h-4 w-4 text-green-500"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Button -->
    <button
      @click="confirmSelection"
      :disabled="multiple ? selectedCampaigns.length === 0 : !selectedCampaign"
      class="px-4 py-3 bg-eggplant-600 hover:bg-eggplant-700 border border-eggplant-600 hover:border-eggplant-700 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm"
    >
      Confirm selection
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";

import { useCampaigns } from "@/queries/campaigns";

interface Campaign {
  id: string;
  isDemo?: boolean;
  lastSyncedAt?: null | string;
  name: string;
}

const props = defineProps<{
  multiple?: boolean;
}>();

const emit = defineEmits<{
  dropdownOpen: [isOpen: boolean];
  select: [campaignId: string | string[]];
}>();

const campaignsQuery = useCampaigns();
const selectedCampaign = ref(""); // For single selection
const selectedCampaigns = ref<string[]>([]); // For multiple selection
const searchQuery = ref("");
const isOpen = ref(false);
const campaignListRef = ref<HTMLElement | null>(null);

const campaigns = computed(() => campaignsQuery.data.value ?? []);

const selectedCampaignName = computed(() => {
  if (props.multiple) {
    if (selectedCampaigns.value.length === 0) return "";
    if (selectedCampaigns.value.includes("all")) return "All campaigns";
    if (selectedCampaigns.value.length === 1) {
      const campaign = campaigns.value.find((c) => c.id === selectedCampaigns.value[0]);
      return campaign?.name || "";
    }
    return `${selectedCampaigns.value.length} campaigns selected`;
  } else {
    if (!selectedCampaign.value) return "";
    const campaign = campaigns.value.find((c) => c.id === selectedCampaign.value);
    return campaign?.name || "";
  }
});

const filteredCampaigns = computed(() => {
  if (!searchQuery.value) return campaigns.value;
  return campaigns.value.filter((campaign) =>
    campaign.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

// Check if any campaigns are not synced yet (excluding demo campaigns)
const hasUnsyncedCampaigns = computed(() => {
  return campaigns.value.some((campaign) => !campaign.isDemo && !campaign.lastSyncedAt);
});

// Check if a campaign is disabled (not synced yet, unless it's a demo campaign)
const isCampaignDisabled = (campaign: Campaign): boolean => {
  // Demo campaigns don't need to be synced, so they're never disabled
  if (campaign.isDemo) return false;
  return !campaign.lastSyncedAt;
};

// Scroll functions
const scrollToBottom = () => {
  if (campaignListRef.value) {
    campaignListRef.value.scrollTo({
      behavior: "smooth",
      top: campaignListRef.value.scrollHeight,
    });
  }
};

const scrollToSelectedCampaign = () => {
  if (!campaignListRef.value || !selectedCampaign.value) return;

  const selectedElement = campaignListRef.value.querySelector(
    `[data-campaign-id="${selectedCampaign.value}"]`
  );
  if (selectedElement) {
    selectedElement.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }
};

// Watch for new campaigns being loaded into the store
watch(
  () => campaigns.value.length,
  async () => {
    if (isOpen.value && !props.multiple) {
      // Only auto-scroll to bottom for new content in single mode
      // In multiple mode, we want to keep "All campaigns" visible
      await nextTick();
      scrollToBottom();
    }
  }
);

// Watch for when dropdown opens with smart scroll behavior
watch(
  () => isOpen.value,
  async (newValue) => {
    // Emit dropdown state change to parent
    emit("dropdownOpen", newValue);

    if (newValue) {
      await nextTick();
      setTimeout(() => {
        if (props.multiple) {
          // In multiple mode, prioritize showing "All campaigns" at the top
          // unless there are specific selections to show
          if (selectedCampaigns.value.length > 0 && !selectedCampaigns.value.includes("all")) {
            // Scroll to show the first selected campaign
            const firstSelected = selectedCampaigns.value[0];
            const selectedElement = campaignListRef.value?.querySelector(
              `[data-campaign-id="${firstSelected}"]`
            );
            if (selectedElement) {
              selectedElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });
            }
          }
          // Otherwise stay at top to show "All campaigns"
        } else {
          // In single mode, scroll to selected item if any
          if (selectedCampaign.value) {
            scrollToSelectedCampaign();
          }
          // Otherwise stay at top
        }
      }, 100); // Small delay to ensure DOM is fully rendered
    }
  }
);

const selectCampaign = (campaign: Campaign) => {
  if (props.multiple) {
    // Handle multiple selection
    if (selectedCampaigns.value.includes("all")) {
      // If "all" is selected, clicking any campaign should deselect "all"
      // and select all campaigns EXCEPT the clicked one
      const allCampaignIds = campaigns.value.map((c) => c.id);
      selectedCampaigns.value = allCampaignIds.filter((id) => id !== campaign.id);
    } else {
      const index = selectedCampaigns.value.indexOf(campaign.id);
      if (index > -1) {
        // Remove if already selected
        selectedCampaigns.value.splice(index, 1);
      } else {
        // Add if not selected
        selectedCampaigns.value.push(campaign.id);
      }
    }
  } else {
    // Handle single selection
    selectedCampaign.value = campaign.id;
    isOpen.value = false;
    searchQuery.value = "";
  }
};

const toggleSelectAll = () => {
  if (selectedCampaigns.value.includes("all")) {
    // Deselect all
    selectedCampaigns.value = [];
  } else {
    // Select all
    selectedCampaigns.value = ["all"];
  }
};

const confirmSelection = () => {
  if (props.multiple) {
    if (selectedCampaigns.value.length > 0) {
      emit("select", selectedCampaigns.value);
      isOpen.value = false;
    }
  } else {
    if (selectedCampaign.value) {
      emit("select", selectedCampaign.value);
    }
  }
};

const clearSearch = () => {
  searchQuery.value = "";
};

// Global click handler to close dropdown when clicking outside
const handleGlobalClick = (event: Event) => {
  if (!isOpen.value) return;

  const target = event.target as HTMLElement;
  const isInsideComponent = target.closest("[data-campaign-picker]");

  if (!isInsideComponent) {
    isOpen.value = false;
  }
};

// Keyboard support
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape" && isOpen.value) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleGlobalClick, true);
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("click", handleGlobalClick, true);
  document.removeEventListener("keydown", handleKeydown);
});
</script>
